import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, CalendarDays, Clock, MapPin, Ticket, Navigation, Bell, Repeat, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_COLORS } from "@/components/CategoryIcon";
import { formatEventDate, formatEventTime } from "@/components/EventCard";
import { recurrenceLabel, nextOccurrences } from "@/lib/events";

export default function EventDetail() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [place, setPlace] = useState(null);

  const remindSaved = user && event && user.saved_events?.includes(event.id);
  const toggleRemind = async () => {
    if (!user) return toast.error("Sign in to get a reminder");
    await api.post(`/favorites/events/${event.id}`);
    await refreshUser();
    toast.success(remindSaved ? "Reminder removed" : "Saved! We'll email you a reminder before this event.");
  };

  const toggleEventHidden = async () => {
    try {
      const next = !event.is_hidden;
      await api.put(`/events/${event.id}`, { is_hidden: next });
      setEvent((ev) => ({ ...ev, is_hidden: next }));
      toast.success(next ? "Hidden from the site" : "Now visible on the site");
    } catch { toast.error("Couldn't update visibility"); }
  };
  const canEdit = user && (user.role === "admin" || user.role === "editor");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/events/${id}`).then(async ({ data }) => {
      setEvent(data);
      if (data.place_id) {
        try {
          const p = await api.get(`/places/${data.place_id}`);
          setPlace(p.data);
        } catch (err) { console.warn("Linked place could not be loaded:", err); }
      }
    });
  }, [id]);

  if (!event) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  const color = CATEGORY_COLORS[event.category];
  const destination = event.address || (place && place.address);

  return (
    <div className="pb-16">
      <div className="relative">
        <img src={event.cover_image} alt={event.title} className="max-h-[70vh] min-h-[40vh] w-full object-cover md:h-[56vh]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/10" />
        <Link to="/events" className="absolute left-4 top-4 flex items-center gap-1 rounded-full bg-black/40 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm">
          <ChevronLeft size={16} /> Events
        </Link>
        {canEdit && (
          <div className="absolute right-4 top-4 flex items-center gap-2">
            <button onClick={toggleEventHidden} data-testid="hide-event-btn"
              className="flex items-center gap-1.5 rounded-full bg-white/90 px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm transition-transform hover:scale-105">
              {event.is_hidden ? <><Eye size={16} /> Show</> : <><EyeOff size={16} /> Hide</>}
            </button>
            <Link to={`/admin?editEvent=${event.id}`} data-testid="edit-event-btn"
              className="flex items-center gap-1.5 rounded-full bg-white/90 px-4 py-2.5 text-sm font-semibold text-primary shadow-sm transition-transform hover:scale-105">
              <Pencil size={16} /> Edit
            </Link>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 mx-auto max-w-4xl px-4 pb-6 md:px-8">
          <span className="mb-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: `${color}dd` }}>
            {event.category}
          </span>
          <h1 className="font-serif text-4xl font-bold text-white text-shadow-soft md:text-5xl">{event.title}</h1>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 md:px-8">
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="flex items-center gap-2 text-sm font-semibold"><CalendarDays size={16} /> Date</p>
            <p className="mt-1 text-muted-foreground">{formatEventDate(event.start_datetime)}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="flex items-center gap-2 text-sm font-semibold"><Clock size={16} /> Time</p>
            <p className="mt-1 text-muted-foreground">
              {formatEventTime(event.start_datetime)}{event.end_datetime ? ` – ${formatEventTime(event.end_datetime)}` : ""} ET
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="flex items-center gap-2 text-sm font-semibold"><Ticket size={16} /> Price</p>
            <p className="mt-1 text-muted-foreground">{event.is_free ? "Free" : event.price_info || "See tickets"}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {event.ticket_url && (
            <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" data-testid="event-tickets-btn"
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
              <Ticket size={16} /> Get Tickets
            </a>
          )}
          {destination && (
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`}
              target="_blank" rel="noopener noreferrer" data-testid="event-directions-btn"
              className="flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors hover:border-primary">
              <Navigation size={16} /> Directions
            </a>
          )}
          <button onClick={toggleRemind} data-testid="event-remind-btn"
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${remindSaved ? "bg-secondary text-secondary-foreground" : "border border-border bg-card hover:border-primary"}`}>
            <Bell size={16} className={remindSaved ? "fill-secondary-foreground" : ""} /> {remindSaved ? "Reminder on" : "Remind me"}
          </button>
        </div>

        <h2 className="mb-2 mt-8 font-serif text-2xl font-semibold">About this event</h2>
        <p className="leading-relaxed text-muted-foreground">{event.description}</p>

        {event.is_recurring && (
          <div data-testid="event-series" className="mt-8 rounded-2xl border border-border bg-card p-5">
            <div className="mb-2 flex items-center gap-2">
              <Repeat size={16} className="text-secondary" />
              <h2 className="font-serif text-xl font-semibold">Recurring series · {recurrenceLabel(event)}</h2>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              This event repeats {event.recurrence === "monthly" ? "monthly" : "weekly"}. Upcoming dates:
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {nextOccurrences(event, 6).map((d, i) => (
                <li key={i} data-testid={`series-date-${i}`} className="flex items-center gap-2 rounded-xl bg-secondary/10 px-3 py-2 text-sm">
                  <CalendarDays size={14} />
                  <span className="font-medium">{d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                  <span className="text-muted-foreground">{d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} ET</span>
                </li>
              ))}
            </ul>
            <button onClick={toggleRemind} data-testid="event-follow-series-btn"
              className={`mt-4 flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${remindSaved ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground hover:opacity-90"}`}>
              <Bell size={15} className={remindSaved ? "fill-secondary-foreground" : ""} />
              {remindSaved ? "Following this series" : "Follow series & get reminders"}
            </button>
            <p className="mt-2 text-xs text-muted-foreground">
              {remindSaved
                ? "We'll email you before the next date, and again before each one that follows."
                : "We'll email you before the next upcoming date each time it comes around."}
            </p>
          </div>
        )}

        {(event.venue_name || destination) && (
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={15} /> {event.venue_name}{destination ? ` · ${destination}` : ""}
          </p>
        )}

        {place && (
          <Link to={`/place/${place.slug}`} className="card-hover mt-8 flex items-center gap-4 rounded-2xl border border-border bg-card p-4">
            <img src={place.cover_image} alt={place.name} className="h-16 w-16 rounded-xl object-cover" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Venue</p>
              <p className="font-serif text-lg font-bold">{place.name}</p>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
